import {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useReducer,
    useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { RESET as REDUX_RESET } from '@/store/constants';
import {
    tpmReducer,
    initialState,
    TPM_ACTIONS,
    REQUEST_TIMEOUT,
    SESSION_EXPIRED_TIME,
} from './tpmReducer';

export const TpmContext = createContext(null);

/** Initial state for activate/deactivate number lists */
const initialNumberList = [{ number: '', status: 'IDLE' }];

/**
 * TPM Provider with useReducer for local state management
 * Redux is only used for:
 * 1. Resuming state when user navigates away and comes back
 * 2. Clearing state on logout/company change
 */
export const TpmProvider = ({ children }) => {
    const [state, dispatch] = useReducer(tpmReducer, initialState);
    const reduxDispatch = useDispatch();

    // Activate/Deactivate number lists (used by ActivateNumbers table)
    const [telephoneNumbers, setTelephoneNumbers] = useState(initialNumberList);
    const [deactivateNumbers, setDeactivateNumbers] =
        useState(initialNumberList);

    const tpmServiceState = useSelector((s) => s.service.tpm);

    // Restore state from Redux on mount (for resume functionality)
    useEffect(() => {
        if (tpmServiceState?.number && tpmServiceState?.currentStep > 0) {
            const currentTime = Date.now();
            const lastUpdate = tpmServiceState.lastUpdate || 0;
            const timeElapsed = currentTime - lastUpdate;

            // Only restore if not expired (30 min session timeout)
            if (timeElapsed < SESSION_EXPIRED_TIME) {
                // Map Redux state to context state
                let screen = 'NUMBER_INPUT';
                if (tpmServiceState.currentStep === 1) {
                    screen =
                        tpmServiceState.operatorStatus === 'found'
                            ? 'VERIFICATION'
                            : 'LOADING';
                } else if (tpmServiceState.currentStep === 2) {
                    screen = 'VERIFICATION';
                } else if (tpmServiceState.formState === 'error') {
                    screen = 'ERROR';
                }

                dispatch({
                    type: TPM_ACTIONS.RESTORE_STATE,
                    payload: {
                        screen,
                        phoneNumber: tpmServiceState.number,
                        requestStartTime: tpmServiceState.lastUpdate,
                        lastCodeSentTime: tpmServiceState.lastUpdate,
                    },
                });
            } else {
                // Session expired, reset Redux
                reduxDispatch({ type: REDUX_RESET });
            }
        }
        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync countries from Redux
    useEffect(() => {
        if (tpmServiceState?.countries && !state.countries) {
            dispatch({
                type: TPM_ACTIONS.SET_COUNTRIES,
                payload: tpmServiceState.countries,
            });
        }
    }, [tpmServiceState?.countries, state.countries]);

    // Check for request timeout (15 min)
    useEffect(() => {
        if (state.screen === 'LOADING' && state.requestStartTime) {
            const checkTimeout = () => {
                const elapsed = Date.now() - state.requestStartTime;
                if (elapsed >= REQUEST_TIMEOUT) {
                    dispatch({
                        type: TPM_ACTIONS.VALIDATION_FAILURE,
                        payload:
                            'Request timed out. The operator validation has expired.',
                    });
                }
            };

            const interval = setInterval(checkTimeout, 1000);
            return () => clearInterval(interval);
        }
    }, [state.screen, state.requestStartTime]);

    // Action creators that wrap dispatch
    const actions = useMemo(
        () => ({
            goToScreen: (screen) =>
                dispatch({ type: TPM_ACTIONS.GO_TO_SCREEN, payload: screen }),

            setPhoneNumber: (phoneNumber) =>
                dispatch({
                    type: TPM_ACTIONS.SET_PHONE_NUMBER,
                    payload: phoneNumber,
                }),

            startValidation: (phoneNumber) =>
                dispatch({
                    type: TPM_ACTIONS.START_VALIDATION,
                    payload: { phoneNumber },
                }),

            validationSuccess: () =>
                dispatch({ type: TPM_ACTIONS.VALIDATION_SUCCESS }),

            validationFailure: (error) =>
                dispatch({
                    type: TPM_ACTIONS.VALIDATION_FAILURE,
                    payload: error,
                }),

            setVerificationCode: (code) =>
                dispatch({
                    type: TPM_ACTIONS.SET_VERIFICATION_CODE,
                    payload: code,
                }),

            submitCodeStart: () =>
                dispatch({ type: TPM_ACTIONS.SUBMIT_CODE_START }),

            submitCodeSuccess: () =>
                dispatch({ type: TPM_ACTIONS.SUBMIT_CODE_SUCCESS }),

            submitCodeFailure: (error) =>
                dispatch({
                    type: TPM_ACTIONS.SUBMIT_CODE_FAILURE,
                    payload: error,
                }),

            resendCode: () => dispatch({ type: TPM_ACTIONS.RESEND_CODE }),

            clearError: () => dispatch({ type: TPM_ACTIONS.CLEAR_ERROR }),

            setCountries: (countries) =>
                dispatch({
                    type: TPM_ACTIONS.SET_COUNTRIES,
                    payload: countries,
                }),

            reset: () => {
                dispatch({ type: TPM_ACTIONS.RESET });
                reduxDispatch({ type: REDUX_RESET });
                // Reset number lists
                setTelephoneNumbers(initialNumberList);
                setDeactivateNumbers(initialNumberList);
            },
        }),
        [reduxDispatch],
    );

    // Backwards compatibility: expose state in old format for gradual migration
    const tpmState = useMemo(
        () => ({
            number: state.phoneNumber,
            status:
                state.screen === 'LOADING'
                    ? 'LOADING'
                    : state.screen === 'ERROR'
                      ? 'ERROR'
                      : state.screen === 'VERIFICATION'
                        ? 'SUCCESS'
                        : 'IDLE',
            error: state.error || '',
            touched: false,
        }),
        [state.phoneNumber, state.screen, state.error],
    );

    const setTpmState = useCallback(
        (updater) => {
            const newState =
                typeof updater === 'function' ? updater(tpmState) : updater;
            if (newState.number !== undefined) {
                dispatch({
                    type: TPM_ACTIONS.SET_PHONE_NUMBER,
                    payload: newState.number,
                });
            }
        },
        [tpmState],
    );

    const value = useMemo(
        () => ({
            // New API
            state,
            dispatch,
            actions,

            // Legacy API for backwards compatibility
            tpmNumber: [tpmState, setTpmState],
            tpmAddress: ['1', () => {}], // deprecated
            tpmActivatedNumber: [telephoneNumbers, setTelephoneNumbers],
            tpmDeactivateNumber: [deactivateNumbers, setDeactivateNumbers],
        }),
        [
            state,
            actions,
            tpmState,
            setTpmState,
            telephoneNumbers,
            deactivateNumbers,
        ],
    );

    return <TpmContext.Provider value={value}>{children}</TpmContext.Provider>;
};
